export type NetworkType = "testnet" | "mainnet";

/** The shape held in redux (and therefore in the persisted blob). */
export type Network = {
  label: string;
  value: NetworkType;
};

/** Only chain the backend accepts today — see `networkNames` in commonTypes.types.ts. */
export const NETWORK_NAME = "polygon";

type NetworkConfig = Network & {
  /** Chip label without the "Polygon " prefix, which is its own span. */
  shortLabel: string;
  /** Block-explorer origin for tx and token links. */
  explorerUrl: string;
};

// Chain-only. Token addresses live in the on-chain registry, not here — see utils/tokens.ts.
export const NETWORKS: Record<NetworkType, NetworkConfig> = {
  mainnet: {
    value: "mainnet",
    label: "Polygon Mainnet",
    shortLabel: "Mainnet",
    explorerUrl: "https://polygonscan.com",
  },
  testnet: {
    value: "testnet",
    label: "Polygon Testnet",
    shortLabel: "Testnet",
    explorerUrl: "https://amoy.polygonscan.com",
  },
};

const CONFIGURED = process.env.REACT_APP_POLYGON_NETWORK;

// Fixed at build time: one deploy serves one chain. Deliberately no default —
// a missing flag must break the build rather than silently ship testnet.
if (CONFIGURED !== "testnet" && CONFIGURED !== "mainnet") {
  throw new Error(
    "REACT_APP_POLYGON_NETWORK must be \"testnet\" or \"mainnet\" (got " +
      `${CONFIGURED ?? "undefined"})`,
  );
}

export const NETWORK_TYPE: NetworkType = CONFIGURED;

/** Seeds the redux slice; the value never changes after boot. */
export const BUILD_NETWORK: Network = {
  label: NETWORKS[NETWORK_TYPE].label,
  value: NETWORK_TYPE,
};

/** Narrow a possibly-undefined stored value to a usable config. */
export function getNetworkConfig(
  value: NetworkType | undefined,
): NetworkConfig {
  return NETWORKS[value ?? NETWORK_TYPE];
}

export function txExplorerUrl(
  value: NetworkType | undefined,
  hash: string | undefined,
): string {
  return `${getNetworkConfig(value).explorerUrl}/tx/${hash ?? ""}`;
}

